<template>
  <div>
    <b-overlay :show="loading" spinner-variant="primary">
      <b-card>
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0">Driver Shifts Management</h4>
          <div>
            <b-button variant="outline-primary" @click="fetchDriverShifts" class="mr-2">
              <feather-icon icon="RefreshCwIcon" class="mr-50" />
              Refresh
            </b-button>
            <b-button variant="primary" @click="showAddShiftsModal">
              <feather-icon icon="PlusIcon" class="mr-50" />
              Add Shifts
            </b-button>
          </div>
        </div>

        <!-- Driver Shifts Status -->
        <div v-if="driverShiftsData" class="mb-4">
          <!-- Assigned Shifts -->
          <div v-if="driverShiftsData.all_shifts && driverShiftsData.all_shifts.length > 0">
            <h5 class="mb-3">Assigned Shifts</h5>
            <b-row>
              <b-col 
                v-for="shift in driverShiftsData.all_shifts" 
                :key="shift.id" 
                md="6" 
                lg="4" 
                class="mb-3"
              >
                <b-card 
                  :class="shift.is_active_now ? 'border-success' : 'border-light'"
                  class="h-100"
                >
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <h6 class="mb-0">{{ shift.name }}</h6>
                    <b-badge 
                      :variant="shift.is_active_now ? 'success' : 'light'"
                      class="text-capitalize"
                    >
                      {{ shift.is_active_now ? 'Active Now' : 'Inactive' }}
                    </b-badge>
                  </div>
                  
                  <div class="mb-2">
                    <small class="text-muted">Days:</small>
                    <div class="font-weight-bold">{{ shift.days }}</div>
                  </div>
                  
                  <div class="mb-2">
                    <small class="text-muted">Time:</small>
                    <div class="font-weight-bold">
                      {{ formatTime(shift.start_time) }} - {{ formatTime(shift.end_time) }}
                    </div>
                  </div>
                  
                  <div class="d-flex justify-content-end">
                    <b-button 
                      variant="outline-danger" 
                      size="sm"
                      @click="removeShift(shift)"
                      :disabled="loading"
                    >
                      <feather-icon icon="XIcon" size="14" />
                      Remove
                    </b-button>
                  </div>
                </b-card>
              </b-col>
            </b-row>
          </div>

          <!-- No Shifts Message -->
          <div v-else class="text-center py-4">
            <feather-icon icon="ClockIcon" size="48" class="text-muted mb-3" />
            <h5 class="text-muted">No shifts assigned</h5>
            <p class="text-muted">This driver doesn't have any shifts assigned yet.</p>
          </div>
        </div>
      </b-card>
    </b-overlay>

    <!-- Add Shifts Modal -->
    <b-modal
      id="add-shifts-modal"
      title="Add Shifts to Driver"
      size="lg"
      hide-footer
    >
      <div v-if="availableShifts.length > 0">
        <h6 class="mb-3">Available Shifts</h6>
        <div class="mb-3">
          <b-form-checkbox-group
            v-model="selectedShifts"
            :options="availableShiftsOptions"
            stacked
          />
        </div>
        
        <div class="d-flex justify-content-end">
          <b-button variant="outline-secondary" @click="cancelAddShifts" class="mr-2">
            Cancel
          </b-button>
          <b-button 
            variant="primary" 
            @click="saveShifts"
            :disabled="loading || selectedShifts.length === 0"
          >
            <b-spinner v-if="loading" small class="mr-1" />
            {{ loading ? 'Adding...' : 'Add Selected Shifts' }}
          </b-button>
        </div>
      </div>
      
      <div v-else class="text-center py-4">
        <feather-icon icon="ClockIcon" size="48" class="text-muted mb-3" />
        <h5 class="text-muted">No available shifts</h5>
        <p class="text-muted">There are no shifts available in the system.</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BOverlay,
  BBadge,
  BRow,
  BCol,
  BModal,
  BFormCheckboxGroup,
  BSpinner,
} from "bootstrap-vue";
import { ref, computed, onMounted } from "@vue/composition-api";
import axiosIns from "@/libs/axios";
import {
  errorNotification,
  successNotification,
} from "@/utils/toast-notifications";

export default {
  components: {
    BCard,
    BButton,
    BOverlay,
    BBadge,
    BRow,
    BCol,
    BModal,
    BFormCheckboxGroup,
    BSpinner,
  },
  props: {
    driverId: {
      type: String,
      required: true,
    },
    loadingDriverData: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { root }) {
    
    const loading = ref(false);
    const driverShiftsData = ref(null);
    const availableShifts = ref([]);
    const selectedShifts = ref([]);

    const availableShiftsOptions = computed(() => {
      // Filter out shifts that are already assigned to this driver
      const assignedShiftIds = driverShiftsData.value?.all_shifts?.map(shift => shift.id) || [];
      const unassignedShifts = availableShifts.value.filter(shift => !assignedShiftIds.includes(shift.id));
      
      return unassignedShifts.map(shift => ({
        value: shift.id,
        text: `${shift.name} - ${shift.days_display} (${formatTime(shift.start_time)} - ${formatTime(shift.end_time)})`,
      }));
    });

    const fetchDriverShifts = async () => {
      if (!props.driverId) return;
      
      try {
        loading.value = true;
        const response = await axiosIns.get(`/drivers/${props.driverId}/shifts/status`);
        
        driverShiftsData.value = response.data.data.data || []
      } catch (error) {
        console.error("Error fetching driver shifts:", error);
        errorNotification("Failed to fetch driver shifts");
      } finally {
        loading.value = false;
      }
    };

    const fetchAvailableShifts = async () => {
      try {
        const response = await axiosIns.get("/drivers/shifts");
        availableShifts.value = response.data.data || [];
      } catch (error) {
        console.error("Error fetching available shifts:", error);
        errorNotification("Failed to fetch available shifts");
      }
    };

    const showAddShiftsModal = () => {
      // Clear any previously selected shifts
      selectedShifts.value = [];
      root.$bvModal.show("add-shifts-modal");
    };

    const saveShifts = async () => {
      if (!props.driverId) return;

      try {
        loading.value = true;
        
        // Only add the selected shifts (no removal logic since we're only showing unassigned shifts)
        if (selectedShifts.value.length > 0) {
          await axiosIns.post(`/drivers/${props.driverId}/shifts`, {
            shift_ids: selectedShifts.value,
          });
        }

        successNotification("Shifts added successfully");
        root.$bvModal.hide("add-shifts-modal");
        await fetchDriverShifts();
      } catch (error) {
        const msg = error?.response?.data?.error?.message || 'Failed to add shifts';
        errorNotification(msg);
      } finally {
        loading.value = false;
      }
    };

    const removeShift = async (shift) => {
      if (!props.driverId) return;

      try {
        loading.value = true;
        await axiosIns.delete(`/drivers/${props.driverId}/shifts`, {
          data: {
            shift_ids: [shift.id],
          },
        });
        successNotification("Shift removed successfully");
        await fetchDriverShifts();
      } catch (error) {
        console.error("Error removing shift:", error);
        errorNotification("Failed to remove shift");
      } finally {
        loading.value = false;
      }
    };

    const cancelAddShifts = () => {
      root.$bvModal.hide("add-shifts-modal");
      selectedShifts.value = [];
    };

    const formatTime = (timeString) => {
      if (!timeString) return "-";
      const date = new Date(timeString);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    };

    const getDayName = (dayNumber) => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[dayNumber] || 'Unknown';
    };

    onMounted(async () => {
      if (props.driverId) {
        await Promise.all([
          fetchDriverShifts(),
          fetchAvailableShifts()
        ]);
      }
    });

    return {
      loading,
      driverShiftsData,
      availableShifts,
      selectedShifts,
      availableShiftsOptions,
      fetchDriverShifts,
      showAddShiftsModal,
      saveShifts,
      removeShift,
      cancelAddShifts,
      formatTime,
      getDayName,
    };
  },
};
</script>

<style scoped>
.border-success {
  border-color: #28a745 !important;
}

.border-light {
  border-color: #e9ecef !important;
}
</style>
